import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../axiosInstance";

const fmt = (v) => {
    const n = parseFloat(v);
    if (isNaN(n) || n === 0) return '--';
    return '₹' + Math.round(n).toLocaleString('en-IN');
};

const fmtDate = (v) => {
    if (!v) return '--';
    try {
        const s = v.toString().substring(0, 10);
        const [y, m, d] = s.split('-');
        return `${d}-${m}-${y}`;
    } catch { return '--'; }
};

const str = (v) => {
    const s = (v ?? '').toString().trim();
    return s || '--';
};

const REPORT_CONFIG = {
    purchase: {
        title: 'Purchase Report',
        color: '#ffc107',
        icon: 'fas fa-store',
        api: '/dealer/list',
        dateField: (r) => r.dealer_datas?.date || r.date,
        totalField: (r) => parseFloat(r.dealer_datas?.buy_price || 0),
        summaryLabel: 'Total Purchase Value',
        columns: ['#', 'Date', 'Reg No', 'Vehicle', 'Model', 'Make Year', 'Color', 'Owners', 'Buy Price', 'Insurance'],
        row: (r, i) => {
            const d = r.dealer_datas || r;
            return [i + 1, fmtDate(d.date), str(r.reg_no), str(d.vehicle_name), str(d.model), str(d.make_year), str(d.color), str(d.noofowners), fmt(d.buy_price), str(d.insurance)];
        },
    },
    sales: {
        title: 'Sales Report',
        color: '#28a745',
        icon: 'fas fa-chart-line',
        api: '/billing/list',
        dateField: (r) => r.date,
        totalField: (r) => parseFloat(r.amount || 0),
        summaryLabel: 'Total Sales Amount',
        columns: ['#', 'Date', 'Bill No', 'Reg No', 'Model', 'Buyer Name', 'City', 'Amount', 'Payment', 'Finance Amt'],
        row: (r, i) => [i + 1, fmtDate(r.date), str(r.bill_no), str(r.reg_no), str(r.bike_model), str(r.buyer_name), str(r.buyer_city), fmt(r.amount), str(r.payment_type1), fmt(r.finance_amount)],
    },
    stock: {
        title: 'Stock Report',
        color: '#007bff',
        icon: 'fas fa-warehouse',
        api: '/stocks/list',
        dateField: (r) => r.dealer?.date || r.date,
        totalField: (r) => parseFloat(r.dealer?.total_price || 0),
        summaryLabel: 'Total Stock Value',
        columns: ['#', 'Date', 'Reg No', 'Model', 'Make Year', 'Color', 'Buy Price', 'Service', 'Total Price'],
        row: (r, i) => {
            const d = r.dealer || r;
            return [i + 1, fmtDate(d.date), str(d.reg_no), str(d.model), str(d.make_year), str(d.color), fmt(d.buy_price), fmt(d.total_service_amount), fmt(d.total_price)];
        },
    },
    outstanding: {
        title: 'Outstanding Report',
        color: '#dc3545',
        icon: 'fas fa-exclamation-circle',
        api: '/finance/open',
        dateField: (r) => r.date,
        totalField: (r) => (parseFloat(r.due_balance || 0) + parseFloat(r.fine_balance || 0)),
        summaryLabel: 'Total Outstanding',
        columns: ['#', 'Date', 'Reg No', 'Borrower', 'City', 'Loan Amt', 'EMI', 'Due Bal', 'Fine Bal', 'Total'],
        row: (r, i) => {
            const db = parseFloat(r.due_balance || 0);
            const fb = parseFloat(r.fine_balance || 0);
            return [i + 1, fmtDate(r.date), str(r.reg_no), str(r.borrower_name), str(r.borrower_city), fmt(r.loan_amount), fmt(r.emi_amount), db > 0 ? fmt(db) : '--', fb > 0 ? fmt(fb) : '--', fmt(db + fb)];
        },
    },
    collection: {
        title: 'Collection Report',
        color: '#17a2b8',
        icon: 'fas fa-receipt',
        api: '/receipt/',
        dateField: (r) => r.createdAt,
        totalField: (r) => parseFloat(r.total || 0),
        summaryLabel: 'Total Collected',
        columns: ['#', 'Date', 'Reg No', 'Recipient', 'City', 'No of Dues', 'Total Paid', 'Payment'],
        row: (r, i) => [i + 1, fmtDate(r.createdAt), str(r.reg_no), str(r.recipient_name), str(r.recipient_city), str(r.noofdue), fmt(r.total), str(r.payment_type)],
    },
};

export default function ReportDetail({ reportType }) {
    const config = REPORT_CONFIG[reportType] || REPORT_CONFIG.collection;

    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const today = now.toISOString().slice(0, 10);

    const [allRows, setAllRows] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(today);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(config.api);
            const raw = res.data;
            const data = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
            setAllRows(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [config.api]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        const filtered = allRows.filter(r => {
            const d = config.dateField(r);
            if (!d) return true;
            const ds = d.toString().substring(0, 10);
            return ds >= from && ds <= to;
        });
        setRows(filtered);
    }, [allRows, from, to, config]);

    const total = rows.reduce((s, r) => s + config.totalField(r), 0);

    const exportCSV = () => {
        const headers = config.columns.join(',');
        const data = rows.map((r, i) => config.row(r, i).map(v => `"${v}"`).join(','));
        const csv = [headers, ...data].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${config.title}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0" style={{ color: config.color }}>
                                <i className={`${config.icon} mr-2`}></i>{config.title}
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/reports">Reports</a></li>
                                <li className="breadcrumb-item active">{config.title}</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    {/* Filter bar */}
                    <div className="card mb-3">
                        <div className="card-body py-2">
                            <div className="row align-items-end">
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <label className="mb-1 small text-muted">From</label>
                                    <input type="date" className="form-control form-control-sm" value={from} onChange={e => setFrom(e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <label className="mb-1 small text-muted">To</label>
                                    <input type="date" className="form-control form-control-sm" value={to} onChange={e => setTo(e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <span className="badge badge-secondary px-3 py-2">{rows.length} records</span>
                                </div>
                                <div className="col-md-3 text-right">
                                    <button className="btn btn-sm btn-success mr-2" onClick={exportCSV} disabled={rows.length === 0}>
                                        <i className="fas fa-file-csv mr-1"></i>Export CSV
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={fetchData}>
                                        <i className="fas fa-sync-alt"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary bar */}
                    {!loading && rows.length > 0 && (
                        <div className="callout mb-3" style={{ borderLeftColor: config.color }}>
                            <h5 style={{ color: config.color }}>
                                {config.summaryLabel}: ₹{Math.round(total).toLocaleString('en-IN')}
                            </h5>
                        </div>
                    )}

                    {/* Table */}
                    <div className="card">
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" style={{ color: config.color }} role="status"></div>
                                </div>
                            ) : rows.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                                    No records in this date range
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm mb-0">
                                        <thead className="thead-light">
                                            <tr>{config.columns.map(c => <th key={c}>{c}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r, i) => (
                                                <tr key={i}>
                                                    {config.row(r, i).map((v, j) => <td key={j}>{v}</td>)}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
